/* eslint-disable react-hooks/exhaustive-deps */
import DeleteModal from "components/DeleteModal";
import ImageModal from "components/ImageModal";
import { useTooltip } from "context/TooltipProvider";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  deleteSuspect,
  fetchSuspectByIncidentRegistrationNumber,
  fetchSuspectJourneys,
} from "services/lealIntityService";
import { CrimeReference, FILE_URL, Investigation, Suspect } from "types";
import SuspectHistoryModal from "./SuspectHistoryModal";
import SuspectJourney, { JourneyStage } from "./SuspectJourney";
import { History } from "lucide-react";
import { deleteIcon, editIcon, viewIcon } from "utils/constant";
import { constructFileUrl, errorHandler } from "utils/helper";
import SuspectDetailInfo from "./modals/SuspectDetailInfo";
import SuspectForm from "./modals/SuspectForm";

// Reusable Action Button Component
const ActionButton: React.FC<{
  onClick: () => void;
  children: React.ReactNode;
  variant?: "primary" | "secondary" | "outline";
  className?: string;
}> = ({ onClick, children, variant = "outline", className = "" }) => {
  const baseClasses =
    "px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 flex items-center gap-2";
  const variants = {
    primary: "bg-red-600 text-white hover:bg-red-700 shadow-sm",
    secondary: "bg-gray-600 text-white hover:bg-gray-700 shadow-sm",
    outline: "border border-gray-300 text-gray-700 hover:bg-gray-50",
  };

  return (
    <button
      className={`${baseClasses} ${variants[variant]} ${className}`}
      onClick={onClick}
    >
      {children}
    </button>
  );
};

type Props = {
  investigation: Investigation;
  incident?: CrimeReference;
  canManageIncidentDetail: boolean;
};

const SuspectTab: React.FC<Props> = ({
  investigation,
  incident,
  canManageIncidentDetail = false,
}) => {
  const { t } = useTranslation();
  const { showTooltip } = useTooltip();
  const [suspects, setSuspects] = useState<Suspect[]>([]);
  const [action, setAction] = useState<"Add" | "Edit">("Add");
  const [loading, setLoading] = React.useState(false);
  const [isOpenForm, setIsOpenForm] = React.useState(false);
  const [isOpenDetail, setIsOpenDetail] = React.useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = React.useState(false);
  const [isOpenDelete, setIsOpenDelete] = React.useState(false);
  const [selectedSuspect, setSelectedSuspect] = React.useState<Suspect>();
  const [journeys, setJourneys] = useState<Record<string, JourneyStage[]>>({});
  const [historyOf, setHistoryOf] = useState<Suspect>();

  const fetchSuspectData = async () => {
    setLoading(true);
    fetchSuspectByIncidentRegistrationNumber(
      incident?.Incident_Registration_number || "",
    )
      .then((data) => {
        setSuspects(data);
        setLoading(false);
      })
      .then(() =>
        // Each suspect's progress; the cards still show if this fails.
        fetchSuspectJourneys(incident?.Incident_Registration_number || "")
          .then(setJourneys)
          .catch(() => setJourneys({})),
      )
      .catch((error) => {
        setLoading(false);
        showTooltip(
          error?.response?.data?.detail || errorHandler(error),
          "error",
        );
      });
  };

  useEffect(() => {
    fetchSuspectData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <span className="text-2xl">🕵️</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {t("suspect_information")}
              </h1>
              <p className="text-gray-600 mt-1">
                {t("manage_suspects_for_incident")}
              </p>
            </div>
          </div>

          {canManageIncidentDetail && incident?.status !== "forwarded" && (
            <ActionButton
              onClick={() => {
                setIsOpenForm(true);
                setAction("Add");
              }}
            >
              ➕ {t("add_suspect")}
            </ActionButton>
          )}
        </div>
      </div>

      {suspects.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <div className="text-center text-gray-500">
            {t("no_suspects_found")}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {suspects.map((suspect, index) => (
            <div
              key={index}
              className="bg-white border border-gray-200 rounded-xl shadow-sm p-6"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4">
                  <img
                    src={(() => {
                      const doc = suspect?.documents?.find(
                        (doc) => doc.document_type === "suspect_picture",
                      );
                      return doc
                        ? constructFileUrl(doc, FILE_URL.SUSPECT)
                        : require("../../../images/imageVector.png");
                    })()}
                    alt="User"
                    className="w-12 h-12 rounded-lg object-cover cursor-pointer"
                    onClick={() => {
                      setSelectedSuspect(suspect);
                      setIsImageModalOpen(true);
                    }}
                  />
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {`${suspect?.first_name} ${suspect?.middle_name} ${suspect?.last_name}`}
                    </h3>
                    <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                      <span>
                        {suspect?.age} {t("years_old_label")}
                      </span>
                      <span>•</span>
                      <span>{suspect?.education_level}</span>
                      <span>•</span>
                      <span>{suspect?.nationality}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setHistoryOf(suspect)}
                    className="p-2 text-gray-600 hover:text-[#AA232F] hover:bg-gray-50 rounded-lg transition-colors"
                    title={t("view_history", { defaultValue: "View history" })}
                    aria-label={t("view_history", { defaultValue: "View history" })}
                  >
                    <History size={18} />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedSuspect(suspect);
                      setIsOpenDetail(true);
                    }}
                    className="p-2 text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded-lg transition-colors"
                    title={t("view_details_button")}
                  >
                    {viewIcon}
                  </button>
                  {canManageIncidentDetail &&
                    incident?.status !== "forwarded" && (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            setAction("Edit");
                            setSelectedSuspect(suspect);
                            setIsOpenForm(true);
                          }}
                          className="p-2 text-gray-600 hover:text-green-600 hover:bg-gray-50 rounded-lg transition-colors"
                          title={t("edit_suspect")}
                        >
                          {editIcon}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedSuspect(suspect);
                            setIsOpenDelete(true);
                          }}
                          className="p-2 text-gray-600 hover:text-red-600 hover:bg-gray-50 rounded-lg transition-colors"
                          title={t("delete_suspect")}
                        >
                          {deleteIcon}
                        </button>
                      </>
                    )}
                </div>
              </div>
              <SuspectJourney
                stages={journeys[String(suspect?.uu_id)]}
                onOpen={() => setHistoryOf(suspect)}
              />
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {historyOf && (
        <SuspectHistoryModal
          suspectId={String(historyOf.uu_id)}
          suspectName={[historyOf.first_name, historyOf.middle_name, historyOf.last_name]
            .filter(Boolean)
            .join(" ")}
          onClose={() => setHistoryOf(undefined)}
        />
      )}
      {isOpenForm && (
        <SuspectForm
          action={action}
          investigation={investigation}
          incident={incident}
          suspect={action === "Edit" ? selectedSuspect : undefined}
          isOpen={isOpenForm}
          onClose={(refetch) => {
            setIsOpenForm(false);
            if (refetch) fetchSuspectData();
          }}
        />
      )}
      {isOpenDetail && (
        <SuspectDetailInfo
          suspect={selectedSuspect}
          isOpen={isOpenDetail}
          onClose={() => setIsOpenDetail(false)}
        />
      )}
      {isImageModalOpen && (
        <ImageModal
          isOpen={isImageModalOpen}
          imageSrc={(() => {
            const doc = selectedSuspect?.documents?.find(
              (doc) => doc.document_type === "suspect_picture",
            );
            return doc
              ? constructFileUrl(doc, FILE_URL.SUSPECT)
              : require("../../../images/imageVector.png");
          })()}
          label={
            selectedSuspect?.first_name +
            " " +
            selectedSuspect?.last_name +
            " " +
            selectedSuspect?.last_name
          }
          onClose={() => setIsImageModalOpen(false)}
        />
      )}
      {selectedSuspect && (
        <DeleteModal
          isOpen={isOpenDelete}
          onConfirm={() => {
            deleteSuspect(selectedSuspect)
              .then(() => {
                showTooltip(t("successfully_deleted"), "success");
                fetchSuspectData();
              })
              .catch((error) => {
                showTooltip(
                  error?.response?.data?.detail ||
                    errorHandler(error) ||
                    t("something_went_wrong_try_again"),
                  "error",
                );
              });
          }}
          onCancel={() => {
            setIsOpenDelete(false);
          }}
          title={t("delete_suspect")}
          message={t("confirm_delete_suspect", {
            name: `${selectedSuspect?.first_name || ""} ${selectedSuspect?.middle_name || ""} ${selectedSuspect?.last_name || ""}`.trim(),
          })}
        />
      )}
    </div>
  );
};

export default SuspectTab;
